import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  FileText, Download, Calendar, Clock, DollarSign, Users, TrendingUp,
  AlertTriangle, CheckCircle, Filter, ChevronDown, Printer, BarChart3
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import LoadingSpinner from '../components/LoadingSpinner';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

const CHART_GRID = { color: 'rgba(255,255,255,0.06)' };
const CHART_TICK = { color: '#9ca3af' };
const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const REPORT_TABS = ['attendance', 'overtime', 'costs', 'staffPerformance'];

export default function Reports({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('attendance');
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [timeRecords, setTimeRecords] = useState([]);
  const [staff, setStaff] = useState([]);
  const [payRules, setPayRules] = useState(null);
  const printRef = useRef();

  // Period calculation
  const dateRange = useMemo(() => {
    if (startDate && endDate) return { start: startDate, end: endDate };
    const now = new Date();
    const periodDays = period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 90 : 365;
    const start = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return { start, end: now.toISOString().split('T')[0] };
  }, [period, startDate, endDate]);

  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      setLoading(true);
      try {
        // Shifts
        const shiftsQ = query(
          collection(db, 'companies', companyId, 'shifts'),
          where('date', '>=', dateRange.start),
          where('date', '<=', dateRange.end),
          orderBy('date')
        );
        const shiftSnap = await getDocs(shiftsQ);
        setShifts(shiftSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Time records
        const trQ = query(
          collection(db, 'companies', companyId, 'timeRecords'),
          where('date', '>=', dateRange.start),
          where('date', '<=', dateRange.end)
        );
        const trSnap = await getDocs(trQ);
        setTimeRecords(trSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Staff
        const staffSnap = await getDocs(collection(db, 'companies', companyId, 'users'));
        setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Pay rules
        try {
          const settingsQ = query(collection(db, 'companies', companyId, 'settings'));
          const settingsSnap = await getDocs(settingsQ);
          if (!settingsSnap.empty) {
            const s = settingsSnap.docs[0].data();
            setPayRules(s.payRules || null);
          }
        } catch { /* ignore */ }
      } catch (err) {
        console.error('Error loading reports data:', err);
      }
      setLoading(false);
    };
    load();
  }, [companyId, dateRange]);

  // ─── Computed data ───
  const attendanceData = useMemo(() => {
    const byDate = {};
    shifts.forEach(s => {
      if (!byDate[s.date]) byDate[s.date] = { scheduled: 0, attended: 0, late: 0, noShow: 0 };
      byDate[s.date].scheduled++;
      const tr = timeRecords.find(r => r.shiftId === s.id);
      if (tr?.clockIn) {
        byDate[s.date].attended++;
        const scheduled = s.startTime?.replace(':', '') || '0000';
        const actual = tr.clockIn?.replace(':', '') || '0000';
        if (parseInt(actual) > parseInt(scheduled) + 15) byDate[s.date].late++;
      } else if (new Date(s.date) < new Date()) {
        byDate[s.date].noShow++;
      }
    });
    return byDate;
  }, [shifts, timeRecords]);

  const overtimeData = useMemo(() => {
    const byStaff = {};
    const weeklyThreshold = payRules?.overtime?.weeklyThreshold || 40;

    staff.forEach(s => {
      byStaff[s.id] = { name: s.displayName || s.email?.split('@')[0] || s.id, totalHours: 0, overtimeHours: 0, shifts: 0, byWeek: {} };
    });

    shifts.forEach(s => {
      if (!s.assignedStaffId || !byStaff[s.assignedStaffId]) return;
      const hours = calcShiftHours(s);
      byStaff[s.assignedStaffId].totalHours += hours;
      byStaff[s.assignedStaffId].shifts++;

      // Group by ISO week
      const d = new Date(s.date);
      const weekKey = `${d.getFullYear()}-W${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)}`;
      if (!byStaff[s.assignedStaffId].byWeek[weekKey]) byStaff[s.assignedStaffId].byWeek[weekKey] = 0;
      byStaff[s.assignedStaffId].byWeek[weekKey] += hours;
    });

    // Calculate overtime per week
    Object.values(byStaff).forEach(staffData => {
      Object.values(staffData.byWeek).forEach(weekHours => {
        if (weekHours > weeklyThreshold) staffData.overtimeHours += weekHours - weeklyThreshold;
      });
    });

    return byStaff;
  }, [shifts, staff, payRules]);

  const costData = useMemo(() => {
    const basePay = payRules?.hourlyRate || 12;
    const overtimeMultiplier = payRules?.overtime?.multiplier || 1.5;
    const byDate = {};
    let totalRegular = 0, totalOvertime = 0;

    shifts.forEach(s => {
      if (!byDate[s.date]) byDate[s.date] = { regular: 0, overtime: 0, total: 0 };
      const hours = calcShiftHours(s);
      const regularHours = Math.min(hours, 8);
      const otHours = Math.max(hours - 8, 0);
      const regularCost = regularHours * basePay;
      const otCost = otHours * basePay * overtimeMultiplier;
      byDate[s.date].regular += regularCost;
      byDate[s.date].overtime += otCost;
      byDate[s.date].total += regularCost + otCost;
      totalRegular += regularCost;
      totalOvertime += otCost;
    });

    return { byDate, totalRegular, totalOvertime, total: totalRegular + totalOvertime };
  }, [shifts, payRules]);

  const staffPerformance = useMemo(() => {
    return staff.map(s => {
      const staffShifts = shifts.filter(sh => sh.assignedStaffId === s.id);
      const staffTR = timeRecords.filter(tr => tr.userId === s.id);
      const attended = staffTR.filter(tr => tr.clockIn).length;
      const late = staffTR.filter(tr => {
        const shift = staffShifts.find(sh => sh.id === tr.shiftId);
        if (!shift?.startTime || !tr.clockIn) return false;
        const diff = parseTime(tr.clockIn) - parseTime(shift.startTime);
        return diff > 15;
      }).length;
      const totalHours = staffShifts.reduce((sum, sh) => sum + calcShiftHours(sh), 0);

      return {
        id: s.id,
        name: s.displayName || s.email?.split('@')[0] || s.id,
        role: s.role || 'staff',
        totalShifts: staffShifts.length,
        attended,
        late,
        noShow: Math.max(0, staffShifts.filter(sh => new Date(sh.date) < new Date()).length - attended),
        totalHours,
        attendanceRate: staffShifts.length > 0 ? ((attended / Math.max(1, staffShifts.filter(sh => new Date(sh.date) < new Date()).length)) * 100).toFixed(1) : '100',
      };
    }).sort((a, b) => b.totalShifts - a.totalShifts);
  }, [staff, shifts, timeRecords]);

  // ─── Chart data builders ───
  const attendanceDates = Object.keys(attendanceData).sort();
  const attendanceChartData = {
    labels: attendanceDates.map(d => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets: [
      { label: t('reports.attended', 'Attended'), data: attendanceDates.map(d => attendanceData[d].attended), backgroundColor: '#10B981', borderRadius: 4 },
      { label: t('reports.late', 'Late'), data: attendanceDates.map(d => attendanceData[d].late), backgroundColor: '#F59E0B', borderRadius: 4 },
      { label: t('reports.noShow', 'No Show'), data: attendanceDates.map(d => attendanceData[d].noShow), backgroundColor: '#EF4444', borderRadius: 4 }
    ]
  };

  const overtimeList = Object.values(overtimeData).filter(s => s.totalHours > 0).sort((a, b) => b.overtimeHours - a.overtimeHours);
  const overtimeChartData = {
    labels: overtimeList.slice(0, 15).map(s => s.name),
    datasets: [
      { label: t('reports.regularHours', 'Regular'), data: overtimeList.slice(0, 15).map(s => Math.max(0, s.totalHours - s.overtimeHours)), backgroundColor: '#2563EB', borderRadius: 4 },
      { label: t('reports.overtimeHours', 'Overtime'), data: overtimeList.slice(0, 15).map(s => s.overtimeHours), backgroundColor: '#EF4444', borderRadius: 4 }
    ]
  };

  const costDates = Object.keys(costData.byDate).sort();
  const costChartData = {
    labels: costDates.map(d => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets: [
      { label: t('reports.regular', 'Regular'), data: costDates.map(d => costData.byDate[d].regular), borderColor: '#2563EB', backgroundColor: 'rgba(37,99,235,0.1)', fill: true, tension: 0.3 },
      { label: t('reports.overtime', 'Overtime'), data: costDates.map(d => costData.byDate[d].overtime), borderColor: '#EF4444', backgroundColor: 'rgba(239,68,68,0.1)', fill: true, tension: 0.3 }
    ]
  };

  const totalAttended = Object.values(attendanceData).reduce((s, d) => s + d.attended, 0);
  const totalScheduled = Object.values(attendanceData).reduce((s, d) => s + d.scheduled, 0);
  const totalLate = Object.values(attendanceData).reduce((s, d) => s + d.late, 0);
  const totalNoShow = Object.values(attendanceData).reduce((s, d) => s + d.noShow, 0);
  const totalOT = Object.values(overtimeData).reduce((s, d) => s + d.overtimeHours, 0);

  // ─── Export ───
  const exportCSV = () => {
    let csv = '';
    if (tab === 'attendance') {
      csv = 'Date,Scheduled,Attended,Late,No Show\n';
      attendanceDates.forEach(d => {
        const a = attendanceData[d];
        csv += `${d},${a.scheduled},${a.attended},${a.late},${a.noShow}\n`;
      });
    } else if (tab === 'overtime') {
      csv = 'Staff,Total Hours,Overtime Hours,Shifts\n';
      overtimeList.forEach(s => {
        csv += `"${s.name}",${s.totalHours.toFixed(1)},${s.overtimeHours.toFixed(1)},${s.shifts}\n`;
      });
    } else if (tab === 'costs') {
      csv = 'Date,Regular Cost,Overtime Cost,Total Cost\n';
      costDates.forEach(d => {
        const c = costData.byDate[d];
        csv += `${d},${c.regular.toFixed(2)},${c.overtime.toFixed(2)},${c.total.toFixed(2)}\n`;
      });
    } else {
      csv = 'Staff,Role,Shifts,Attended,Late,No Show,Hours,Attendance Rate\n';
      staffPerformance.forEach(s => {
        csv += `"${s.name}",${s.role},${s.totalShifts},${s.attended},${s.late},${s.noShow},${s.totalHours.toFixed(1)},${s.attendanceRate}%\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${tab}-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingSpinner />;

  const tabConfig = [
    { id: 'attendance', icon: CheckCircle, label: t('reports.attendance', 'Attendance') },
    { id: 'overtime', icon: Clock, label: t('reports.overtime', 'Overtime') },
    { id: 'costs', icon: DollarSign, label: t('reports.costs', 'Costs') },
    { id: 'staffPerformance', icon: Users, label: t('reports.staffPerformance', 'Staff Performance') },
  ];

  return (
    <div className="space-y-6" ref={printRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <FileText className="text-primary" size={24} />
          {t('reports.title', 'Reports & Analytics')}
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={period} onChange={e => { setPeriod(e.target.value); setStartDate(''); setEndDate(''); }}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-primary [color-scheme:dark]">
            <option value="week" className="bg-slate-700">Last 7 days</option>
            <option value="month" className="bg-slate-700">Last 30 days</option>
            <option value="quarter" className="bg-slate-700">Last 90 days</option>
            <option value="year" className="bg-slate-700">Last year</option>
          </select>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white [color-scheme:dark]" />
          <span className="text-gray-400 text-sm">–</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white [color-scheme:dark]" />
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-2 rounded-lg text-sm font-medium transition">
            <Download size={14} /> CSV
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-gray-300 px-3 py-2 rounded-lg text-sm font-medium transition">
            <Printer size={14} /> {t('reports.print', 'Print')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/10">
        {tabConfig.map(tb => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-medium transition ${tab === tb.id ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}>
              <Icon size={14} /> <span className="hidden sm:inline">{tb.label}</span>
            </button>
          );
        })}
      </div>

      {/* ═══ Attendance Tab ═══ */}
      {tab === 'attendance' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Calendar} label={t('reports.totalScheduled', 'Total Scheduled')} value={totalScheduled} color="text-primary" />
            <StatCard icon={CheckCircle} label={t('reports.attended', 'Attended')} value={totalAttended} color="text-green-400" />
            <StatCard icon={Clock} label={t('reports.late', 'Late Arrivals')} value={totalLate} color="text-amber-400" />
            <StatCard icon={AlertTriangle} label={t('reports.noShows', 'No Shows')} value={totalNoShow} color="text-red-400" />
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">{t('reports.attendanceTrend', 'Attendance Trend')}</h3>
            <div className="h-72">
              <Bar data={attendanceChartData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { labels: { color: '#9ca3af' } } },
                scales: {
                  x: { stacked: true, ticks: CHART_TICK, grid: CHART_GRID },
                  y: { stacked: true, beginAtZero: true, ticks: CHART_TICK, grid: CHART_GRID }
                }
              }} />
            </div>
          </div>
          {/* Attendance rate gauge */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-2">{t('reports.attendanceRate', 'Overall Attendance Rate')}</h3>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold text-green-400">{totalScheduled > 0 ? ((totalAttended / totalScheduled) * 100).toFixed(1) : '100'}%</div>
              <div className="flex-1 h-4 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full transition-all" style={{ width: `${totalScheduled > 0 ? (totalAttended / totalScheduled) * 100 : 100}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Overtime Tab ═══ */}
      {tab === 'overtime' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard icon={Clock} label={t('reports.totalOvertimeHours', 'Total Overtime')} value={`${totalOT.toFixed(1)}h`} color="text-red-400" />
            <StatCard icon={Users} label={t('reports.staffWithOT', 'Staff with OT')} value={overtimeList.filter(s => s.overtimeHours > 0).length} color="text-amber-400" />
            <StatCard icon={TrendingUp} label={t('reports.avgOTPerStaff', 'Avg OT/Staff')} value={`${overtimeList.length > 0 ? (totalOT / overtimeList.filter(s => s.overtimeHours > 0).length || 0).toFixed(1) : 0}h`} color="text-primary" />
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">{t('reports.overtimeByStaff', 'Overtime by Staff')}</h3>
            <div className="h-72">
              <Bar data={overtimeChartData} options={{
                responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                plugins: { legend: { labels: { color: '#9ca3af' } } },
                scales: {
                  x: { stacked: true, beginAtZero: true, ticks: CHART_TICK, grid: CHART_GRID },
                  y: { stacked: true, ticks: { ...CHART_TICK, font: { size: 11 } }, grid: CHART_GRID }
                }
              }} />
            </div>
          </div>
          {/* Overtime table */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-400 p-3">{t('reports.staff', 'Staff')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.shifts', 'Shifts')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.totalHours', 'Total Hours')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.otHours', 'OT Hours')}</th>
                </tr>
              </thead>
              <tbody>
                {overtimeList.slice(0, 20).map(s => (
                  <tr key={s.name} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white">{s.name}</td>
                    <td className="p-3 text-right text-gray-300">{s.shifts}</td>
                    <td className="p-3 text-right text-gray-300">{s.totalHours.toFixed(1)}</td>
                    <td className={`p-3 text-right font-medium ${s.overtimeHours > 0 ? 'text-red-400' : 'text-gray-400'}`}>{s.overtimeHours.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Costs Tab ═══ */}
      {tab === 'costs' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard icon={DollarSign} label={t('reports.totalCost', 'Total Cost')} value={`${payRules?.currency || '£'}${costData.total.toFixed(0)}`} color="text-primary" />
            <StatCard icon={DollarSign} label={t('reports.regularCost', 'Regular Pay')} value={`${payRules?.currency || '£'}${costData.totalRegular.toFixed(0)}`} color="text-green-400" />
            <StatCard icon={DollarSign} label={t('reports.overtimeCost', 'Overtime Pay')} value={`${payRules?.currency || '£'}${costData.totalOvertime.toFixed(0)}`} color="text-red-400" />
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">{t('reports.costTrend', 'Cost Trend')}</h3>
            <div className="h-72">
              <Line data={costChartData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { labels: { color: '#9ca3af' } } },
                scales: {
                  x: { ticks: CHART_TICK, grid: CHART_GRID },
                  y: { beginAtZero: true, ticks: { ...CHART_TICK, callback: v => `${payRules?.currency || '£'}${v}` }, grid: CHART_GRID }
                }
              }} />
            </div>
          </div>
          {/* Cost breakdown pie */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">{t('reports.costBreakdown', 'Cost Breakdown')}</h3>
              <div className="h-48">
                <Doughnut data={{
                  labels: [t('reports.regular', 'Regular'), t('reports.overtime', 'Overtime')],
                  datasets: [{ data: [costData.totalRegular, costData.totalOvertime], backgroundColor: ['#2563EB', '#EF4444'], borderWidth: 0 }]
                }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { labels: { color: '#9ca3af' } } } }} />
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">{t('reports.costSummary', 'Summary')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t('reports.avgDailyCost', 'Avg Daily Cost')}</span><span className="text-white font-medium">{payRules?.currency || '£'}{costDates.length > 0 ? (costData.total / costDates.length).toFixed(2) : '0.00'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t('reports.avgShiftCost', 'Avg Shift Cost')}</span><span className="text-white font-medium">{payRules?.currency || '£'}{shifts.length > 0 ? (costData.total / shifts.length).toFixed(2) : '0.00'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t('reports.otPct', 'OT % of Total')}</span><span className="text-red-400 font-medium">{costData.total > 0 ? ((costData.totalOvertime / costData.total) * 100).toFixed(1) : '0'}%</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t('reports.totalShifts', 'Total Shifts')}</span><span className="text-white font-medium">{shifts.length}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Staff Performance Tab ═══ */}
      {tab === 'staffPerformance' && (
        <div className="space-y-4">
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-left text-gray-400 p-3">{t('reports.staff', 'Staff')}</th>
                  <th className="text-center text-gray-400 p-3">{t('reports.role', 'Role')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.shifts', 'Shifts')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.attended', 'Attended')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.late', 'Late')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.noShows', 'No Shows')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.hours', 'Hours')}</th>
                  <th className="text-right text-gray-400 p-3">{t('reports.attendanceRate', 'Rate')}</th>
                </tr>
              </thead>
              <tbody>
                {staffPerformance.map(s => (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white font-medium">{s.name}</td>
                    <td className="p-3 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary capitalize">{s.role}</span></td>
                    <td className="p-3 text-right text-gray-300">{s.totalShifts}</td>
                    <td className="p-3 text-right text-green-400">{s.attended}</td>
                    <td className="p-3 text-right text-amber-400">{s.late}</td>
                    <td className="p-3 text-right text-red-400">{s.noShow}</td>
                    <td className="p-3 text-right text-gray-300">{s.totalHours.toFixed(1)}</td>
                    <td className="p-3 text-right">
                      <span className={`font-medium ${parseFloat(s.attendanceRate) >= 90 ? 'text-green-400' : parseFloat(s.attendanceRate) >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                        {s.attendanceRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
      <Icon size={20} className={`mx-auto ${color} mb-1`} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-gray-400">{label}</p>
    </div>
  );
}

function calcShiftHours(shift) {
  if (!shift.startTime || !shift.endTime) return 0;
  const start = parseTime(shift.startTime);
  const end = parseTime(shift.endTime);
  const hours = end > start ? (end - start) / 60 : (24 * 60 - start + end) / 60;
  // Subtract breaks
  if (shift.segments) {
    const breakMins = shift.segments
      .filter(s => s.type === 'break')
      .reduce((sum, s) => {
        if (s.startTime && s.endTime) {
          const bs = parseTime(s.startTime);
          const be = parseTime(s.endTime);
          return sum + (be > bs ? be - bs : 0);
        }
        return sum + (s.duration || 0);
      }, 0);
    return Math.max(0, hours - breakMins / 60);
  }
  return hours;
}

function parseTime(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
